/**
 * Bounded LRU of per-history-entry scroll snapshots.
 *
 * Keyed by react-router's `location.key`, which is persisted into
 * `window.history.state` and therefore survives back/forward AND a hard reload.
 *
 * Capped because `navigate(..., { replace: true })` mints a NEW `location.key`
 * on every call — and this app replaces on every filter change. Without a cap,
 * one orphaned snapshot accumulates per filter toggle.
 *
 * Storage is injected so the module stays pure and testable without jsdom.
 */

export interface Snapshot {
  scrollTop: number
  pageDepth: number
  /**
   * The pathname the snapshot was taken on. Required because `location.key` is NOT
   * globally unique: react-router labels the first entry of any fresh document
   * `"default"`, while sessionStorage outlives the document. Without this, deep-scrolling
   * /events and then typing /cameras into the address bar in the same tab would apply the
   * /events snapshot — offset AND page depth — to /cameras, seeding pages of the wrong
   * resource. Consumers must discard a snapshot whose pathname does not match.
   */
  pathname: string
}

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export interface ScrollStore {
  get(key: string): Snapshot | undefined
  set(key: string, snap: Snapshot): void
  size(): number
}

export const SNAPSHOT_CAP = 30
const STORAGE_KEY = 'detercam:scroll'

/**
 * sessionStorage survives reloads within a tab, so a deploy that changes the
 * Snapshot shape can leave stale entries behind. Validate rather than trust —
 * an unchecked cast would let `get()` return null despite its declared type.
 */
function isSnapshot(value: unknown): value is Snapshot {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Partial<Snapshot>
  return (
    typeof v.scrollTop === 'number' && Number.isFinite(v.scrollTop) &&
    typeof v.pageDepth === 'number' && Number.isFinite(v.pageDepth) &&
    typeof v.pathname === 'string' && v.pathname.length > 0
  )
}

export function createScrollStore(storage: StorageLike | null): ScrollStore {
  const map = new Map<string, Snapshot>()

  if (storage) {
    try {
      const raw = storage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed: unknown = JSON.parse(raw)
        if (Array.isArray(parsed)) {
          for (const entry of parsed) {
            if (
              Array.isArray(entry) && entry.length === 2 &&
              typeof entry[0] === 'string' && isSnapshot(entry[1])
            ) {
              map.set(entry[0], entry[1])
            }
          }
        }
      }
    } catch {
      // Corrupt payload, or storage unavailable (Safari private mode).
      // Start empty; scroll restoration degrades, filters still work.
    }
  }

  const persist = () => {
    if (!storage) return
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify([...map]))
    } catch {
      // Quota exceeded or storage disabled — memory-only from here.
    }
  }

  return {
    get(key) {
      const snap = map.get(key)
      if (snap) {
        map.delete(key)   // promote: most-recently-used goes last
        map.set(key, snap)
      }
      return snap
    },
    set(key, snap) {
      map.delete(key)
      map.set(key, snap)
      while (map.size > SNAPSHOT_CAP) {
        const oldest = map.keys().next().value
        if (oldest === undefined) break
        map.delete(oldest)
      }
      persist()
    },
    size() {
      return map.size
    },
  }
}

function defaultStorage(): StorageLike | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null // accessing sessionStorage throws in some privacy modes
  }
}

export const scrollStore = createScrollStore(defaultStorage())
